import { useApiVersion } from '@smartsash/api-client';
import { VersionOverlay } from '@smartsash/ui';
import { useTranslation } from 'react-i18next';

import { env } from '@/env';

/**
 * Feeds the shared `VersionOverlay` with admin's build version and the backend's (HS-1208).
 *
 * `__APP_VERSION__` is inlined by `@smartsash/vite-preset` from this app's `package.json`.
 * While the API read is pending — or after it fails — its value is the empty string, which
 * `VersionOverlay` drops, so the overlay shows admin's version alone rather than a dangling
 * "API:" label. Nothing here surfaces the failure: the overlay is a diagnostic, and a toast
 * about it would be noise.
 *
 * Mounted only when `SHOW_VERSION_OVERLAY` is on, so the flag also suppresses the request.
 *
 * `diagnosticsExtra` carries what the package cannot know: which backend this bundle was built
 * against, and whether it is a production build. Both are inlined at build time (`src/env.ts`),
 * so a copied report names the deployment even when the API read failed (HS-1222).
 */
export function AppVersionOverlay() {
  const { t } = useTranslation();
  const { data: apiVersion } = useApiVersion();

  return (
    <VersionOverlay
      entries={[
        { label: t('version.app'), value: __APP_VERSION__ },
        { label: t('version.api'), value: apiVersion ?? '' },
      ]}
      copyable
      diagnosticsExtra={{
        'API origin': env.VITE_API_BASE_URL,
        'Build mode': import.meta.env.MODE,
      }}
    />
  );
}
